import React, { useCallback, useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { queueCount } from '../db/offlineStore';
import { drainQueue } from '../services/syncEngine';
import ConflictResolutionModal from './ConflictResolutionModal';

/**
 * Connection-status pill. Shows one of three states:
 *   🟢  online + no queued items
 *   🟡  syncing (draining the queue after reconnect)
 *   🔴  offline (with a badge for how many actions are queued)
 * Auto-drains the queue whenever ``online`` transitions from false → true.
 */
const OfflineBanner = ({ onSynced }) => {
  const online = useOnlineStatus();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [wasOnline, setWasOnline] = useState(online);
  const [showConflictModal, setShowConflictModal] = useState(false);

  const refreshCount = useCallback(async () => {
    setPending(await queueCount());
  }, []);

  const runDrain = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await drainQueue();
      setLastResult(r);
      if (r.ok > 0 && typeof onSynced === 'function') onSynced();
    } finally {
      await refreshCount();
      setSyncing(false);
    }
  }, [onSynced, refreshCount]);

  // Poll queue count so external enqueues (from driver actions) update the badge.
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 3000);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Auto-drain on offline → online transition.
  useEffect(() => {
    if (online && !wasOnline) {
      runDrain();
    }
    setWasOnline(online);
  }, [online, wasOnline, runDrain]);

  // Steady state: online + no queue → hide (don't nag).
  if (online && pending === 0 && !syncing && !lastResult) return null;

  // Success flash after a successful drain.
  if (online && pending === 0 && lastResult && (lastResult.ok > 0 || (lastResult.conflicts && lastResult.conflicts.length > 0))) {
    const conflictCount = (lastResult.conflicts || []).length;
    // Only auto-dismiss when there are no conflicts to review — otherwise
    // we want the driver to see the "Review changes" affordance until they act.
    if (conflictCount === 0) {
      setTimeout(() => setLastResult(null), 6000);
    }
    return (
      <>
        <div
          data-testid="offline-banner-synced"
          className={`fixed top-3 inset-x-3 md:inset-x-auto md:right-4 md:top-4 md:w-80 z-50 rounded-md shadow-lg border px-4 py-3 flex items-start gap-3 ${
            conflictCount > 0
              ? 'bg-amber-50 border-amber-200'
              : 'bg-emerald-50 border-emerald-200'
          }`}
        >
          <CheckCircle className={`w-5 h-5 flex-shrink-0 ${conflictCount > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
          <div className={`flex-1 text-sm ${conflictCount > 0 ? 'text-amber-900' : 'text-emerald-900'}`}>
            {lastResult.ok > 0 && (
              <div>Synced {lastResult.ok} action{lastResult.ok === 1 ? '' : 's'}.</div>
            )}
            {conflictCount > 0 && (
              <div data-testid="offline-banner-conflicts" className="mt-0.5">
                <div className="text-xs">
                  <strong>{conflictCount}</strong> load{conflictCount === 1 ? '' : 's'} changed on dispatch&apos;s side while you were offline.
                </div>
                <button
                  type="button"
                  onClick={() => setShowConflictModal(true)}
                  data-testid="offline-banner-review-conflicts"
                  className="mt-1.5 text-xs font-semibold text-amber-900 underline hover:no-underline"
                >
                  Review changes
                </button>
              </div>
            )}
          </div>
          {conflictCount > 0 && (
            <button
              type="button"
              onClick={() => setLastResult(null)}
              aria-label="Dismiss"
              data-testid="offline-banner-dismiss"
              className="text-amber-600 hover:text-amber-800 text-lg leading-none"
            >
              &times;
            </button>
          )}
        </div>
        {showConflictModal && (
          <ConflictResolutionModal
            conflicts={lastResult.conflicts || []}
            onClose={() => {
              setShowConflictModal(false);
              // Clear the banner once the driver has acknowledged the review.
              setLastResult(null);
            }}
          />
        )}
      </>
    );
  }

  const tone = !online
    ? { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', icon: 'text-red-600', label: 'Offline' }
    : { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: 'text-amber-600', label: syncing ? 'Syncing' : 'Pending sync' };
  const Icon = !online ? WifiOff : syncing ? RefreshCw : Wifi;

  return (
    <div
      data-testid="offline-banner"
      data-online={online ? '1' : '0'}
      className={`fixed top-3 inset-x-3 md:inset-x-auto md:right-4 md:top-4 md:w-80 z-50 rounded-md shadow-lg border px-4 py-3 flex items-center gap-3 ${tone.bg} ${tone.border}`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${tone.icon} ${syncing ? 'animate-spin' : ''}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${tone.text}`}>
          {tone.label}
          {pending > 0 && (
            <span
              data-testid="offline-queue-count"
              className="ml-2 inline-flex items-center justify-center rounded-full bg-white/70 px-2 py-0.5 text-xs font-bold"
            >
              {pending} queued
            </span>
          )}
        </div>
        <div className={`text-xs mt-0.5 ${tone.text} opacity-80`}>
          {!online
            ? 'Your changes will sync when you\u2019re back online.'
            : syncing
              ? 'Sending queued updates…'
              : 'Reconnected — retrying pending actions.'}
        </div>
      </div>
      {online && pending > 0 && !syncing && (
        <button
          data-testid="offline-retry-now"
          onClick={runDrain}
          className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline"
        >
          Retry now
        </button>
      )}
    </div>
  );
};

export default OfflineBanner;
